import React from 'react';
import { Shield, Clock, FileCheck, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ProtectionSystemSection = () => {
  const { t } = useTranslation();
  
  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Título Principal */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('protectionSystem.doubleLayerTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('protectionSystem.doubleLayerSubtitle')}
          </p>
        </motion.div>

        {/* Cards de Proteção */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          
          {/* Card 1 - Certificado Digital Imediato */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-blue-200"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-4 rounded-2xl">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('protectionSystem.digitalCertificateTitle')}
                </h3>
                <p className="text-blue-600 font-semibold">{t('protectionSystem.instantaneous')}</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              Assim que você registra sua obra, o sistema gera <strong>automaticamente</strong> um certificado com:
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-lg">
                <FileCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Carimbo de Tempo</p>
                  <p className="text-sm text-gray-600">Prova exata de quando você criou</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">{t('protectionSystem.cryptoHash')}</p>
                  <p className="text-sm text-gray-600">{t('protectionSystem.cryptoHashDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-lg">
                <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Assinatura Eletrônica</p>
                  <p className="text-sm text-gray-600">{t('protectionSystem.legalSignature')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-xl text-center">
              <p className="font-bold text-lg">{t('protectionSystem.availableInSeconds')}</p>
              <p className="text-sm text-blue-100">{t('protectionSystem.instantProtection')}</p>
            </div>
          </motion.div>

          {/* Card 2 - Registro Oficial BN */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-purple-200"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-4 rounded-2xl">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Registro Oficial na BN
                  </h3>
                  <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    OPCIONAL
                  </span>
                </div>
                <p className="text-purple-600 font-semibold">{t('protectionSystem.uponRequest')}</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              O registro na Biblioteca Nacional é <strong>totalmente opcional</strong> e apenas ocorrerá mediante <strong>solicitação expressa do autor</strong>. A cada <strong>120 dias</strong>, as obras solicitadas são protocoladas oficialmente. Para viabilizar o registro coletivo, você poderá autorizar que a plataforma figure como <strong>coautora técnica com 1%</strong> (participação simbólica, exclusivamente para legitimidade formal do registro), garantindo:
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 bg-purple-50 p-3 rounded-lg">
                <Award className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">{t('protectionSystem.publicRecognition')}</p>
                  <p className="text-sm text-gray-600">Registro oficial do governo federal</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-purple-50 p-3 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Arquivamento Permanente</p>
                  <p className="text-sm text-gray-600">{t('protectionSystem.culturalPreservation')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-purple-50 p-3 rounded-lg">
                <FileCheck className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">{t('protectionSystem.totalRenunciation')}</p>
                  <p className="text-sm text-gray-600">A plataforma abre mão de royalties, licenciamento, execução pública, sincronização e distribuição digital</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-xl text-center">
              <p className="font-bold text-lg">✅ Você mantém 100% dos ganhos econômicos</p>
              <p className="text-sm text-purple-100">Coautoria técnica 1% apenas para legitimidade formal do registro</p>
            </div>
          </motion.div>
        </div>

        {/* Barra de Destaque */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white rounded-2xl p-8 text-center shadow-2xl"
        >
          <h3 className="text-3xl font-bold mb-4">
            🎯 Por que escolher nossa plataforma?
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div>
              <p className="font-bold text-xl mb-2">⚡ Rápido</p>
              <p className="text-green-100">Certificado em segundos, não em semanas</p>
            </div>
            <div>
              <p className="font-bold text-xl mb-2">🆓 Acessível</p>
              <p className="text-green-100">Registro BN gratuito incluído</p>
            </div>
            <div>
              <p className="font-bold text-xl mb-2">🔒 Seguro</p>
              <p className="text-green-100">Dupla camada: digital + oficial</p>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default ProtectionSystemSection;
